import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import MissionService from "../../../../../service/MissionService";
import {
    setLightingInCanvas,
    showToastMessage,
    loadGltfFromUrl,
    loadGltfModelWithMap,
    MAX_MODEL_FILE_SIZE_BYTES,
    validateModelFileSize,
    validateGlbFileIntegrity,
    processZipOrManualModelFiles,
} from "../../../../../utils/commonFunctions";
import CustomSelect from "../../../../CommonComponents/CustomSelect";
import { ModalDeviderLine } from "../../../../simulate/svgs/Svgs";
import { UploadModelSvg, CloseIcon } from "./RedundancySvg";
import AnimationLoader from "./loading_model.json"
import Lottie from "react-lottie-player";
import CommonCanvas from "../../../../CommonComponents/CommonCanvas";

type AxisOption = {
    label: string;
    value: string;
};

const getDefaultNadirOrbitAxis = (busSize: number) => {
    const nadirAxis = (busSize > 6 && busSize < 27) ? 'Z' : 'X'
    const orbitAxis = (busSize > 6 && busSize < 27) ? 'X' : 'Y'
    return {
        nadir: { label: nadirAxis, value: nadirAxis },
        orbit: { label: orbitAxis, value: orbitAxis }
    }
}

const SatelliteModelUploader = ({
    busSize,
    setSatelliteModelUploader,
    setModel,
    model
}: {
    busSize: number
    setSatelliteModelUploader: (value: boolean) => void;
    model: any;
    setModel: (value: any) => void;
}) => {

    const { sat_id: satelliteId, mission_id: missionId } = useParams();
    const [loading, setLoading] = useState(false);
    const [modelUploading, setModelUploading] = useState(false);
    const [models, setModels] = useState<any>();
    const [modelUrl, setModelUrl] = useState<string | null>(null);
    // Tracks whether the current previewed model passed parse/load checks.
    const [isModelValid, setIsModelValid] = useState(false);
    // Stores the latest validation/load failure reason for user-facing feedback.
    const [modelValidationError, setModelValidationError] = useState<string | null>(null);

    // .glb: single File, uploaded as-is 
    const [glbFile, setGlbFile] = useState<File | null>(null);

    //  .gltf+.bin or .zip: zip File to upload 
    const [zipFile, setZipFile] = useState<File | null>(null);
    // extracted files 
    const [extractedFiles, setExtractedFiles] = useState<File[] | null>(null);

    const fileInputRef = useRef<HTMLInputElement | null>(null);
    // Tracks preview blob URLs so they can be revoked across reselection/unmount.
    const previewObjectUrlsRef = useRef<string[]>([]);

    const [axisOrientation, setAxisOrientation] = useState(getDefaultNadirOrbitAxis(busSize));

    const AXIS_OPTIONS: AxisOption[] = [
        { label: 'Z', value: 'Z' },
        { label: '-Z', value: '-Z' },
        { label: 'Y', value: 'Y' },
        { label: '-Y', value: '-Y' },
        { label: 'X', value: 'X' },
        { label: '-X', value: '-X' }
    ];

    useEffect(() => {
        if (!glbFile) return;
        const objectUrl = URL.createObjectURL(glbFile);
        trackPreviewObjectUrl(objectUrl);
        setModelUrl(objectUrl);
        setLoading(true);
        setIsModelValid(false);
        loadGltfFromUrl(objectUrl, {
            targetSize: 2,
            onError: (message: string) => setModelValidationError(message)
        }).then((scene) => {
            setModels(scene);
            setIsModelValid(!!scene);
            if (scene) setModelValidationError(null);
            setLoading(false);
        });
    }, [glbFile]);

    // ── zip/gltf+bin 
    useEffect(() => {
        if (!extractedFiles || extractedFiles.length === 0) return;

        const mainFile = extractedFiles.find(
            file => file.name.endsWith(".glb") || file.name.endsWith(".gltf")
        );
        if (!mainFile) return;
        // setFileName(mainFile.name)
        // Build filename → blob URL map for all extracted files
        const fileMap: Record<string, string> = {};
        extractedFiles.forEach(file => {
            const objectUrl = URL.createObjectURL(file);
            fileMap[file.name] = objectUrl;
            trackPreviewObjectUrl(objectUrl);
        });

        const mainBlobUrl = fileMap[mainFile.name];
        setModelUrl(mainBlobUrl);
        setLoading(true);
        setIsModelValid(false);
        loadGltfModelWithMap(mainBlobUrl, fileMap, {
            targetSize: 2,
            onError: (message: string) => setModelValidationError(message)
        }).then((scene) => {
            setModels(scene);
            setIsModelValid(!!scene);
            if (scene) setModelValidationError(null);
            setLoading(false);
        });
    }, [extractedFiles]);

    const openFileUploader = () => {
        if (fileInputRef.current) fileInputRef.current.click();
    };

    // Registers a blob URL for later cleanup.
    const trackPreviewObjectUrl = (url: string) => {
        previewObjectUrlsRef.current.push(url);
    };

    // Revokes all tracked preview blob URLs.
    const revokePreviewObjectUrls = () => {
        previewObjectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
        previewObjectUrlsRef.current = [];
    };

    // Ensures preview URLs are cleaned if the uploader is closed/unmounted.
    useEffect(() => {
        return () => {
            revokePreviewObjectUrls();
        };
    }, []);

    // ── Upload ───────────────────────────────────────────────────────────────

    const uploadInChunks = async (fileToUpload: File, satelliteId: string) => {
        if (!fileToUpload || !satelliteId) {
            showToastMessage("error", "Satellite ID or file missing");
            return;
        }
        setModelUploading(true);

        const chunkSize = 10 * 1024 * 1024;
        const totalChunks = Math.ceil(fileToUpload.size / chunkSize);

        try {
            for (let i = 0; i < totalChunks; i++) {
                const start = i * chunkSize;
                const end = Math.min(fileToUpload.size, start + chunkSize);
                const chunk = fileToUpload.slice(start, end);

                const formData = new FormData();
                formData.append('fileName', fileToUpload.name);
                formData.append('chunkNumber', i.toString());
                formData.append('object', chunk);
                formData.append('totalParts', totalChunks.toString());
                formData.append('nadirAxis', axisOrientation.nadir?.value);
                formData.append('orbitAxis', axisOrientation.orbit?.value);

                await MissionService.postThreedModel(satelliteId, missionId, formData);
            }
            // Preserve the active GLB preview URL so parent view can continue using it post-save.
            if (fileToUpload.name.toLowerCase().endsWith('.glb') && modelUrl) {
                previewObjectUrlsRef.current = previewObjectUrlsRef.current.filter((url) => url !== modelUrl);
            }
            setModel({ ...model, modelName: fileToUpload.name, modelUrl: modelUrl, zip: zipFile ?? null });
            setModelUploading(false);
            setSatelliteModelUploader(false);
        } catch (err: any) {
            showToastMessage("error", err?.response?.data?.message || "Model upload failed");
            setLoading(false);
            setModelUploading(false);
            throw err;
        }
    };



    const submit_model = async () => {
        if (!satelliteId) return;
        // Hard guard: saving is allowed only after a successful model validation/load cycle.
        if (!isModelValid) {
            showToastMessage("error", modelValidationError || "Please upload a valid 3D model before saving");
            return;
        }
        if (glbFile) {
            if (!validateModelFileSize(glbFile, MAX_MODEL_FILE_SIZE_BYTES)) return;
            await uploadInChunks(glbFile, satelliteId);
        } else if (zipFile) {
            if (!validateModelFileSize(zipFile, MAX_MODEL_FILE_SIZE_BYTES)) return;
            await uploadInChunks(zipFile, satelliteId);
        }
    };

    // ── Validation & routing ─────────────────────────────────────────────────

    const handleFileValidationAndUpload = async (selectedFiles: File[]) => {
        if (!selectedFiles || selectedFiles.length === 0) return;

        // Reset stale preview state before processing a new selection.
        // setLoading(true) here ensures no flicker: models clears and loading
        // activates in the same render, preventing the placeholder from showing.
        revokePreviewObjectUrls();
        setModels(null);
        setModelUrl(null);
        setIsModelValid(false);
        setModelValidationError(null);
        setGlbFile(null);
        setZipFile(null);
        setExtractedFiles(null);
        setLoading(true);

        const allowedExts = [".glb", ".gltf", ".bin", ".zip"];
        for (const f of selectedFiles) {
            const ext = f.name.substring(f.name.lastIndexOf(".")).toLowerCase();
            if (!allowedExts.includes(ext)) {
                showToastMessage("error", "Only .glb, .gltf, .bin, and .zip formats are supported");
                setLoading(false);
                return;
            }
            if (!validateModelFileSize(f, MAX_MODEL_FILE_SIZE_BYTES)) {
                setLoading(false);
                return;
            }
        }

        // Case 1: Single .glb
        if (selectedFiles.length === 1 && selectedFiles[0].name.toLowerCase().endsWith(".glb")) {
            // Validate first to prevent corrupted-.glb loader flicker; start loading only for valid previews.
            const validation = await validateGlbFileIntegrity(selectedFiles[0]);
            if (!validation?.isValid) {
                const reason = validation?.message || "Failed to parse .glb file";
                setModelValidationError(reason);
                showToastMessage("error", reason);
                setGlbFile(null);
                setZipFile(null);
                setExtractedFiles(null);
                setLoading(false);
                return;
            }
            setModelValidationError(null);
            setZipFile(null);
            setExtractedFiles(null);
            setLoading(true);
            setGlbFile(selectedFiles[0]);
            return;
        }

        // Case 2: Single .zip — use common helper
        if (selectedFiles.length === 1 && selectedFiles[0].name.toLowerCase().endsWith(".zip")) {
            const result = await processZipOrManualModelFiles(selectedFiles, "zip");
            if (result.success) {
                setZipFile(result.zipFile);
                setExtractedFiles(result.extractedFiles);
            } else {
                setModelValidationError(result.message || "Failed to process zip model");
                setLoading(false);
            }
            return;
        }

        // Case 3: .gltf + .bin selected manually — use common helper
        const result = await processZipOrManualModelFiles(selectedFiles, "manual");
        if (result.success) {
            setZipFile(result.zipFile);
            setExtractedFiles(result.extractedFiles);
        } else {
            setModelValidationError(result.message || "Failed to process model files");
            setLoading(false);
        }
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(event.target.files || []) as File[];
        handleFileValidationAndUpload(files);
        event.target.value = "";
    };

    const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        const files = Array.from(event.dataTransfer.files) as File[];
        handleFileValidationAndUpload(files);
    };

    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
    };

    const hasFile = !!(glbFile || zipFile);
    const canSaveModel = hasFile && isModelValid && !modelUploading && !loading;

    const getOppositeAxis = (axis: string) =>
        axis.startsWith('-') ? axis.slice(1) : `-${axis}`;

    const getFilteredAxisOptions = (otherAxisValue: string) =>
        AXIS_OPTIONS.filter(
            opt =>
                opt.value !== otherAxisValue &&
                opt.value !== getOppositeAxis(otherAxisValue)
        );

    const updateAxis = (type: "nadir" | "orbit", selected: AxisOption) => {
        const otherType = type === "nadir" ? "orbit" : "nadir";
        setAxisOrientation(prev => {
            const validOptions = getFilteredAxisOptions(selected.value);
            const isStillValid = validOptions.some(opt => opt.value === prev[otherType].value);
            return {
                ...prev,
                [type]: selected,
                [otherType]: isStillValid ? prev[otherType] : validOptions[0]
            };
        });
    };

    return (
        <div className="satellite_model_container">
            <div className="satellite_model_uploader">
                <div className="header_frame">
                    <div className="header_content">
                        <span>Import Satellite Model</span>
                        <div className="grp_class" onClick={() => setSatelliteModelUploader(false)}>
                            <CloseIcon />
                        </div>
                    </div>
                    <ModalDeviderLine />
                </div>

                <div className="middle_frame">
                    <div className="heading_content">
                        <div className="model_label_row">
                            <span>Model</span>
                        </div>

                        {models ? (
                            <div className="model_core_frame">
                                <CommonCanvas
                                    model={models}
                                    controls={{ minDistance: 5, maxDistance: 100, target: [0, 0, 0] }}
                                    setLightingInCanvas={setLightingInCanvas}
                                    onReplaceModel={openFileUploader}
                                    modelName={glbFile?.name || zipFile?.name || "No name"}
                                    disabled={modelUploading}
                                />
                            </div>
                        ) : (
                            <div
                                className="model_core_frame"
                                onClick={openFileUploader}
                                onDrop={handleDrop}
                                onDragOver={handleDragOver}
                            >
                                {loading ? (
                                    <div className="model_loading_container">
                                        <Lottie key={String(loading)} loop animationData={AnimationLoader} play />
                                    </div>
                                ) : (
                                    <div className="model_upload_container">
                                        <UploadModelSvg />
                                    </div>
                                )}
                                <div className="model_text_container">
                                    {loading ? (
                                        <span>Loading Model...</span>
                                    ) : (
                                        <>
                                            <span>Drag & drop file here or click here to upload</span>
                                            <span>Supported: .glb, .gltf + .bin (select both), .zip — Max size: 100 MB</span>
                                        </>
                                    )}
                                </div>
                            </div>
                        )}

                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".glb,.gltf,.bin,.zip"
                            multiple
                            style={{ display: "none" }}
                            onChange={handleFileChange}
                        />
                    </div>

                    <div className={`model_content ${hasFile ? '' : 'loading_state'}`}>
                        <span>Manual Orientation</span>
                    </div>

                    <div className={`bottom_content ${hasFile ? '' : 'loading_state'}`}>
                        <div className="axis_orientation_class">
                            <span>Nadir Axis</span>
                            <div className="axis_input_class">
                                <CustomSelect
                                    options={getFilteredAxisOptions(axisOrientation.orbit.value)}
                                    placeholder="Select Nadir Axis"
                                    value={axisOrientation.nadir}
                                    onChange={(e) => updateAxis("nadir", e)}
                                    controllHeight="24px"
                                    menuHeight="100px"
                                    isDisabled={modelUploading}
                                />
                            </div>
                        </div>
                        <div className="axis_orientation_class">
                            <span>Orbit Axis</span>
                            <div className="axis_input_class">
                                <CustomSelect
                                    options={getFilteredAxisOptions(axisOrientation.nadir.value)}
                                    placeholder="Select Orbit Axis"
                                    value={axisOrientation.orbit}
                                    onChange={(e) => updateAxis("orbit", e)}
                                    controllHeight="24px"
                                    menuHeight="100px"
                                    isDisabled={modelUploading}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="bottom_frame">
                    <ModalDeviderLine />
                    <div className="button_wrapper">
                        <div className="action_buttons" onClick={() => setSatelliteModelUploader(false)}>
                            Close
                        </div>
                        <div
                            className={`action_buttons ${canSaveModel ? 'active_save_button' : 'disable_save_button'}`}
                            onClick={() => { if (canSaveModel) submit_model(); }}
                        >
                            {modelUploading ? 'Saving...' : 'Save'}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
export default SatelliteModelUploader;